import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { SearchSuggestionsResponseDTO } from '../dto/searchSuggestion.dto';
import { adaptSearchResponseDtoToDomain, searchResultsToPlainObject } from '../mappers/search.mapper';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import { SearchRepository } from "../../domain/interfaces/SearchRepository";
import { SearchResults } from "../../domain/models/SearchSuggestion";
import type { AppDispatch } from "@/app/store/store";
import { executeQuery } from "@/shared/features/query/utils/queryUtils";

/**
 * Search API Slice
 * Creates query endpoints for search feature
 */
const searchApiSlice = createApiSlice({
  clientId: 'searchApi',
  tagTypes: ['Search'],
  endpoints: {
    getSearchSuggestions: {
      url: (args: unknown) => {
        const { query } = args as { query: string; allowEmpty?: boolean };
        const encodedQuery = encodeURIComponent(query);
        return `/search/enhanced-suggest?q=${encodedQuery}&format=json&search_text=${encodedQuery}`;
      },
      method: 'GET',
      transformResponse: (response: unknown): Record<string, unknown> => {
        // DTO → Domain (applies domain logic) → Plain Object (for Redux)
        const domainModel = adaptSearchResponseDtoToDomain(response as SearchSuggestionsResponseDTO);
        return searchResultsToPlainObject(domainModel);
      },
      keepUnusedDataFor: 300,
      providesTags: ['Search'],
    },
  },
});

export const searchApi = searchApiSlice.queryClient;
export const searchApiRedux = searchApiSlice.queryClient as ReduxQueryClient;

/**
 * Search Repository Adapter
 * Implements SearchRepository using query client
 */
export class searchAdapter implements SearchRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchSuggestions(query: string, allowEmpty = false): Promise<SearchResults> {
    try {
      const initiateResult = searchApi.endpoints.getSearchSuggestions.initiate({ 
        query: query.trim(), 
        allowEmpty 
      });
      // ExecuteQuery returns plain object from Redux, convert to domain model using same method
      const plainObject = await executeQuery<Record<string, unknown>>(initiateResult, this.dispatch);
      return adaptSearchResponseDtoToDomain(plainObject);
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
      return new SearchResults({
        suggestions: [],
        products: { productTitle: "", list: [] },
      });
    }
  }
}
